/**
 * The selected Account, for the dashboard's Account switcher (Feature: user-and-data-management,
 * Requirement 5.8).
 *
 * A User may be a member of several Accounts. The API resolves exactly one per request, chosen by the
 * `x-soroush-account` header, so the SPA needs one place holding *which* — and, just as importantly,
 * **the role in that Account**.
 *
 * ## The role is the reason this context exists
 *
 * Before the switcher, `useCanWrite` read `role` off the ID token, and that was correct because the
 * only Account a request could name was the token's own. It is now wrong in both directions: a User who
 * is `admin` at home and `viewer` in a shared Account would be shown every write control and refused by
 * every write; a User who is `viewer` at home and `editor` elsewhere would be denied affordances they
 * actually have. So the role travels with the selection, and the write gates read it from here.
 *
 * ## Bootstrapping, and why this provider builds its own client
 *
 * `useApiClient` depends on the selected Account, so it cannot be used to *discover* the Accounts.
 * This provider therefore constructs a bare client for the single `GET /v1/accounts` call. That is
 * sound rather than a workaround: the accounts route is the one route whose answer is not
 * Account-scoped, so it needs no header to be meaningful.
 *
 * ## A stale selection must not look like a broken dashboard
 *
 * A membership can be revoked between visits, and access stops on the very next request with no cache
 * window (Req 5.9). A selection persisted from a previous visit could therefore name an Account the
 * User can no longer reach, and every request would answer `403` — which reads as "the app is broken",
 * not as "you were removed". The persisted value is therefore validated against the freshly fetched
 * list and silently reset to the home Account when it is no longer there.
 *
 * Persistence is `sessionStorage`, not `localStorage`: per-tab, so two tabs can hold two Accounts
 * without fighting, and gone at the end of the session rather than outliving a sign-out.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from 'react-oidc-context';

import { createApiClient } from './api/client';
import type { AccountSummary } from './api/types';
import { identityFromUser } from './auth/identity';
import { useConfig } from './config-context';

/** Where the selection is remembered. Per-tab (`sessionStorage`), see the module doc. */
const STORAGE_KEY = 'soroush.selectedAccountId';

export interface SelectedAccount {
  /** The Account every request acts as, or `undefined` before the list has loaded. */
  readonly accountId: string | undefined;
  /**
   * The role **in that Account** — from the membership row for a shared Account, from the claim for the
   * home one. `undefined` until known, which the write gates read as no access (fail closed).
   */
  readonly role: string | undefined;
  /** Every Account the User can act as. Home first; empty until loaded. */
  readonly accounts: readonly AccountSummary[];
  /** True while the list is in flight. The switcher renders disabled rather than absent. */
  readonly loading: boolean;
  /**
   * Why the list could not be loaded, if it could not.
   *
   * Surfaced rather than swallowed: with no list the app still works against the home Account, so this
   * is a degraded switcher and not a broken dashboard, and saying so beats an empty dropdown.
   */
  readonly error: string | undefined;
  /** Select an Account. Ignores an id that is not in {@link accounts}. */
  readonly selectAccount: (accountId: string) => void;
}

const AccountContext = createContext<SelectedAccount | undefined>(undefined);

function readStoredSelection(): string | undefined {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    // Storage can throw in a hardened browser configuration. A switcher that cannot remember its
    // selection is a minor loss; one that crashes the app on load is not.
    return undefined;
  }
}

function writeStoredSelection(accountId: string | undefined): void {
  try {
    if (accountId === undefined) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, accountId);
  } catch {
    /* see readStoredSelection */
  }
}

export interface AccountProviderProps {
  children: ReactNode;
}

/**
 * Load the User's Accounts and provide the selected one to descendants.
 *
 * Must sit inside the OIDC provider (it needs the token) and outside anything calling
 * {@link import('./api/use-api-client').useApiClient}, which reads the selection.
 */
export function AccountProvider({ children }: AccountProviderProps): JSX.Element {
  const { apiEndpoint } = useConfig();
  const auth = useAuth();
  const user = auth.user;
  const { accountId: homeAccountId, role: homeRole } = identityFromUser(user);

  const [accounts, setAccounts] = useState<readonly AccountSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>(readStoredSelection);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);

  /**
   * The effect below depends on this **string**, never on the `user` object.
   *
   * `react-oidc-context` hands back a fresh `user` reference on each state transition, and an effect
   * that fetches and then setStates on an unstable dependency re-runs after its own render — an endless
   * fetch loop that never settles, since every pass sets `loading` back to true. A memo can tolerate an
   * unstable dependency (`useApiClient` does, deliberately, so a token renew rebuilds the client); a
   * fetching effect cannot. The token is what the call actually needs, and it is a string.
   */
  const idToken = user?.id_token;

  useEffect(() => {
    if (idToken === undefined || idToken.length === 0) return;
    let active = true;
    setLoading(true);
    setError(undefined);

    // A bare client: no Account header, because `GET /v1/accounts` is not Account-scoped. Built here
    // rather than through `useApiClient`, which depends on the selection this call establishes.
    const client = createApiClient(apiEndpoint, () => idToken);
    client
      .listAccounts()
      .then((response) => {
        if (!active) return;
        const loaded = response.accounts;
        setAccounts(loaded);
        // Validate the persisted selection against what the User can reach NOW. A membership revoked
        // since the last visit would otherwise 403 every request under a label that still looks right.
        setSelectedId((current) =>
          current !== undefined && loaded.some((account) => account.accountId === current)
            ? current
            : loaded[0]?.accountId,
        );
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : 'Could not load your accounts');
        // Fall back to the home Account from the claim so the dashboard still works. The switcher is
        // degraded; the app is not.
        setAccounts(
          homeAccountId === undefined
            ? []
            : [{ accountId: homeAccountId, role: (homeRole ?? 'viewer') as AccountSummary['role'], via: 'home-claim' }],
        );
        setSelectedId(homeAccountId);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // Every dependency is a string, so this runs once per sign-in and once per token renew — not once
    // per render. See the note on `idToken`.
  }, [apiEndpoint, idToken, homeAccountId, homeRole]);

  useEffect(() => {
    writeStoredSelection(selectedId);
  }, [selectedId]);

  const selectAccount = useCallback(
    (accountId: string) => {
      // Only an Account in the list. A caller cannot widen access this way — the server verifies
      // membership regardless — but accepting an arbitrary id would produce a switcher stuck on an
      // Account that 403s everything, which is a worse failure than ignoring the click.
      if (!accounts.some((account) => account.accountId === accountId)) return;
      setSelectedId(accountId);
    },
    [accounts],
  );

  const value = useMemo<SelectedAccount>(() => {
    const selected = accounts.find((account) => account.accountId === selectedId);
    return {
      accountId: selected?.accountId ?? selectedId,
      // From the selected Account, NOT from the token — see the module doc.
      role: selected?.role,
      accounts,
      loading,
      error,
      selectAccount,
    };
  }, [accounts, selectedId, loading, error, selectAccount]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

/** Read the selected Account. Throws if used outside an {@link AccountProvider}. */
export function useSelectedAccount(): SelectedAccount {
  const selected = useContext(AccountContext);
  if (selected === undefined) {
    throw new Error('useSelectedAccount must be used within an AccountProvider');
  }
  return selected;
}
